// LeadEngine.jsx - Live Lead Conversion Engine UI
// 4 tabs: Live Feed / Sources / Settings / Analytics

import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import FormTemplatesManager from "./FormTemplatesManager";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
  hot:"#dc2626", warm:"#f59e0b", cold:"#3b82f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export default function LeadEngine() {
  const [tab, setTab] = useState("feed");

  return (
    <div>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>🔥 Lead Conversion Engine</div>
        <div style={{fontSize:12,color:C.muted}}>Live feed of leads from your ad campaigns - automated welcome flow, nurture sequence, and AI scoring</div>
      </div>

      <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:"1px solid "+C.border,width:"fit-content",marginBottom:18,flexWrap:"wrap"}}>
        {[
          ["feed", "🔴 Live Feed"],
          ["sources", "🔌 Sources"],
          ["templates", "📋 Form Templates"],
          ["response", "⏱ Response Times"],
          ["settings", "⚙️ Auto-Welcome"],
          ["analytics", "📊 Analytics"],
        ].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} style={{
            padding:"7px 16px",fontSize:12,fontWeight:tab===v?700:500,
            color:tab===v?"#fff":C.muted,
            background:tab===v?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"none",
            borderRadius:7,border:"none",cursor:"pointer",fontFamily:F,
          }}>{l}</button>
        ))}
      </div>

      {tab === "feed" && <LiveFeed />}
      {tab === "sources" && <SourcesTab />}
      {tab === "templates" && <FormTemplatesManager />}
      {tab === "response" && <ResponseTimeDashboard />}
      {tab === "settings" && <SettingsTab />}
      {tab === "analytics" && <AnalyticsTab />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LIVE FEED
// ════════════════════════════════════════════════════════════
function LiveFeed() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    load();
    // Real-time subscription
    const channel = supabase
      .channel("leads-feed")
      .on("postgres_changes", { event: "*", schema: "public", table: "leads" }, (payload) => {
        load();
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [filter]);

  const load = async () => {
    let q = supabase.from("leads").select("*").order("created_at", { ascending: false }).limit(100);
    if (filter === "hot") q = q.eq("lead_temperature", "hot");
    if (filter === "today") q = q.gte("created_at", new Date(new Date().setHours(0,0,0,0)).toISOString());
    if (filter === "unbooked") q = q.eq("booked", false).eq("opted_out", false);
    const { data } = await q;
    setLeads(data || []);
  };

  const filtered = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.full_name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.phone?.includes(q) || l.interest?.toLowerCase().includes(q);
  });

  const todayCount = leads.filter(l => {
    const created = new Date(l.created_at);
    const today = new Date(); today.setHours(0,0,0,0);
    return created >= today;
  }).length;

  const hotCount = leads.filter(l => l.lead_temperature === "hot").length;

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:14}}>
        <Stat label="Today" value={todayCount} color={C.gold}/>
        <Stat label="🔥 Hot" value={hotCount} color={C.hot}/>
        <Stat label="Total Loaded" value={leads.length} color={C.blue}/>
        <Stat label="Live Status" value="🟢 Active" color={C.green}/>
      </div>

      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search name, email, phone, interest..." style={{...inp, flex:1, minWidth:240}}/>
        <select value={filter} onChange={e=>setFilter(e.target.value)} style={{...inp, width:"auto"}}>
          <option value="all">All recent (100)</option>
          <option value="today">Today only</option>
          <option value="hot">🔥 Hot leads</option>
          <option value="unbooked">Unbooked</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div style={{padding:40,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:10}}>
          <div style={{fontSize:36,marginBottom:8}}>📭</div>
          <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>No leads yet</div>
          <div style={{fontSize:12,color:C.muted}}>Connect a source in the "Sources" tab to start receiving leads</div>
        </div>
      ) : (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,overflow:"hidden"}}>
          {filtered.map(l => (
            <LeadRow key={l.id} lead={l} onClick={()=>setSelectedLead(l)}/>
          ))}
        </div>
      )}

      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={()=>{setSelectedLead(null); load();}}/>}
    </div>
  );
}

function LeadRow({ lead, onClick }) {
  const tempColor = lead.lead_temperature === "hot" ? C.hot : lead.lead_temperature === "cold" ? C.cold : C.warm;
  const tempIcon = lead.lead_temperature === "hot" ? "🔥" : lead.lead_temperature === "cold" ? "❄️" : "✨";
  const minsAgo = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 60000);
  const ago = minsAgo < 60 ? `${minsAgo}m ago` : minsAgo < 1440 ? `${Math.floor(minsAgo/60)}h ago` : `${Math.floor(minsAgo/1440)}d ago`;
  const isFresh = minsAgo < 30;

  return (
    <div onClick={onClick} style={{
      padding:"12px 16px",
      borderBottom:"1px solid "+C.border,
      borderLeft:"3px solid "+tempColor,
      cursor:"pointer",
      display:"flex",alignItems:"center",gap:12,
      background: isFresh ? "rgba(180,140,60,0.04)" : "transparent",
      transition:"background 0.15s",
    }}
    onMouseEnter={e=>e.currentTarget.style.background=C.goldBg}
    onMouseLeave={e=>e.currentTarget.style.background=isFresh?"rgba(180,140,60,0.04)":"transparent"}>
      <div style={{fontSize:20}}>{tempIcon}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text}}>{lead.full_name || "—"}</div>
          {isFresh && <span style={{fontSize:9,padding:"1px 6px",background:C.green,color:"#fff",borderRadius:50,fontWeight:700}}>NEW</span>}
          {lead.welcome_sms_sent && <span style={{fontSize:9}} title="Welcome SMS sent">📱</span>}
          {lead.welcome_email_sent && <span style={{fontSize:9}} title="Welcome email sent">📧</span>}
          {lead.booked && <span style={{fontSize:9,padding:"1px 6px",background:C.green,color:"#fff",borderRadius:50,fontWeight:700}}>BOOKED ✓</span>}
        </div>
        <div style={{fontSize:11,color:C.muted}}>
          {lead.interest && <>{lead.interest} · </>}
          {lead.phone || lead.email || "no contact"}
        </div>
      </div>
      <div style={{textAlign:"right",flexShrink:0}}>
        <div style={{fontSize:11,fontWeight:700,color:tempColor}}>Score {lead.lead_score || 50}</div>
        <div style={{fontSize:10,color:C.muted}}>{lead.source || "unknown"}</div>
      </div>
      <div style={{textAlign:"right",fontSize:10,color:C.dim,minWidth:60}}>{ago}</div>
    </div>
  );
}

function LeadDetailModal({ lead, onClose }) {
  const [activities, setActivities] = useState([]);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("lead_activities").select("*").eq("lead_id", lead.id).order("created_at", { ascending: false });
      setActivities(data || []);
    })();
  }, [lead.id]);

  const sendNow = async (type) => {
    setUpdating(true);
    try {
      const fn = type === "sms" ? "lead-welcome-sms" : "lead-welcome-email";
      // Reset flag so it sends
      const flag = type === "sms" ? "welcome_sms_sent" : "welcome_email_sent";
      await supabase.from("leads").update({ [flag]: false }).eq("id", lead.id);
      const { data, error } = await supabase.functions.invoke(fn, { body: { lead_id: lead.id } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      alert(`${type.toUpperCase()} sent!`);
    } catch (e) {
      alert("Failed: " + (e.message || e));
    } finally {
      setUpdating(false);
    }
  };

  const markBooked = async () => {
    await supabase.from("leads").update({ booked: true, status: "booked" }).eq("id", lead.id);
    await supabase.from("lead_activities").insert({
      lead_id: lead.id,
      activity_type: "status_changed",
      channel: "system",
      content: "Marked as booked",
    });
    onClose();
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:540,width:"100%",maxHeight:"90vh",overflow:"auto"}}>
        <div style={{padding:"18px 22px",borderBottom:"1px solid "+C.border,background:"linear-gradient(135deg,"+C.goldBg+","+C.light+")"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:20,fontWeight:800,color:C.text,fontFamily:S}}>{lead.full_name}</div>
              <div style={{fontSize:11,color:C.muted}}>From {lead.source} · {new Date(lead.created_at).toLocaleString()}</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:20}}>×</button>
          </div>
        </div>

        <div style={{padding:22}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <Stat label="Score" value={lead.lead_score || 50} color={lead.lead_temperature === "hot" ? C.hot : lead.lead_temperature === "cold" ? C.cold : C.warm}/>
            <Stat label="Est. Value" value={lead.estimated_value ? "$"+(+lead.estimated_value).toFixed(0) : "—"} color={C.green}/>
          </div>

          <div style={{padding:12,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:8,marginBottom:14}}>
            <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Interest</div>
            <div style={{fontSize:14,fontWeight:600,color:C.text}}>{lead.interest || "Not specified"}</div>
            {lead.notes && <div style={{fontSize:11,color:C.muted,marginTop:6}}>{lead.notes}</div>}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <Field label="Phone">{lead.phone || "—"}</Field>
            <Field label="Email">{lead.email || "—"}</Field>
          </div>

          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
            {lead.phone && (
              <a href={`tel:${lead.phone}`} style={{padding:"8px 14px",background:C.green,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,fontFamily:F,textDecoration:"none"}}>📞 Call</a>
            )}
            {lead.phone && (
              <a href={`sms:${lead.phone}`} style={{padding:"8px 14px",background:C.blue,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,fontFamily:F,textDecoration:"none"}}>💬 Text</a>
            )}
            <button onClick={()=>sendNow("sms")} disabled={updating || !lead.phone} style={{padding:"8px 14px",background:C.gold,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:updating?"not-allowed":"pointer",fontFamily:F,opacity:!lead.phone?0.5:1}}>📱 Send Welcome SMS</button>
            <button onClick={()=>sendNow("email")} disabled={updating || !lead.email} style={{padding:"8px 14px",background:C.purple,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:updating?"not-allowed":"pointer",fontFamily:F,opacity:!lead.email?0.5:1}}>📧 Send Welcome Email</button>
            {!lead.booked && (
              <button onClick={markBooked} style={{padding:"8px 14px",background:C.green,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>✓ Mark Booked</button>
            )}
          </div>

          <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Activity Timeline</div>
          {activities.length === 0 ? (
            <div style={{padding:14,textAlign:"center",color:C.muted,fontSize:11}}>No activity yet</div>
          ) : (
            <div style={{maxHeight:280,overflow:"auto"}}>
              {activities.map(a => (
                <div key={a.id} style={{padding:"8px 10px",borderLeft:"3px solid "+C.borderGold,marginBottom:6,background:C.light,borderRadius:6}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.text}}>{a.activity_type.replace(/_/g," ").toUpperCase()}</div>
                  {a.content && <div style={{fontSize:11,color:C.muted,marginTop:2,whiteSpace:"pre-wrap"}}>{a.content.substring(0,200)}</div>}
                  <div style={{fontSize:10,color:C.dim,marginTop:3}}>{new Date(a.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// SOURCES TAB
// ════════════════════════════════════════════════════════════
function SourcesTab() {
  const [sources, setSources] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const { data } = await supabase.from("lead_webhook_sources").select("*").order("id");
    setSources(data || []);
  };

  const SUPABASE_URL = "https://fuoejykgpnfphrvqjuub.supabase.co";

  const buildUrl = (s) => `${SUPABASE_URL}/functions/v1/lead-webhook?source=${s.source_key}&secret=${s.webhook_secret}`;

  const copy = (s) => {
    const url = buildUrl(s);
    navigator.clipboard.writeText(url);
    setCopiedId(s.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div>
      <div style={{padding:14,background:"linear-gradient(135deg,rgba(180,140,60,0.08),rgba(180,140,60,0.04))",border:"1px solid "+C.borderGold,borderRadius:10,marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,marginBottom:5,fontFamily:S}}>How to connect Facebook Lead Ads</div>
        <ol style={{margin:0,paddingLeft:20,fontSize:11,color:C.text,lineHeight:1.7}}>
          <li>Copy the Facebook webhook URL below</li>
          <li>Go to <a href="https://business.facebook.com/settings" target="_blank" rel="noreferrer" style={{color:C.gold}}>Business Manager</a> → Apps → Lead Center → Webhooks</li>
          <li>Paste your URL and verify (we auto-respond to verification challenge)</li>
          <li>Subscribe to "leadgen" events on your Lead form</li>
          <li>You're done — leads will appear in this dashboard within 30 seconds</li>
        </ol>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {sources.map(s => (
          <div key={s.id} style={{padding:16,background:C.card,border:"1px solid "+C.border,borderRadius:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.text}}>{s.name}</div>
                <div style={{fontSize:11,color:C.muted}}>
                  {s.total_leads_received || 0} leads received
                  {s.last_received_at && <> · last: {new Date(s.last_received_at).toLocaleDateString()}</>}
                </div>
              </div>
              <span style={{padding:"3px 10px",background:s.is_active?C.greenL:C.light,color:s.is_active?C.green:C.muted,borderRadius:50,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                {s.is_active?"Active":"Inactive"}
              </span>
            </div>

            <div style={{position:"relative"}}>
              <input value={buildUrl(s)} readOnly style={{...inp, fontFamily:"monospace", fontSize:11, paddingRight:90, background:C.light}}/>
              <button onClick={()=>copy(s)} style={{position:"absolute",top:5,right:5,padding:"5px 12px",background:copiedId===s.id?C.green:C.gold,color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                {copiedId===s.id?"✓ Copied":"📋 Copy"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// SETTINGS TAB
// ════════════════════════════════════════════════════════════
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("lead_engine_settings").select("*").eq("id", 1).single();
      setSettings(data);
    })();
  }, []);

  const update = (k, v) => setSettings(s => ({...s, [k]: v}));

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("lead_engine_settings").update({
      welcome_sms_enabled: settings.welcome_sms_enabled,
      welcome_sms_template: settings.welcome_sms_template,
      welcome_sms_delay_seconds: +settings.welcome_sms_delay_seconds,
      welcome_email_enabled: settings.welcome_email_enabled,
      welcome_email_subject: settings.welcome_email_subject,
      nurture_enabled: settings.nurture_enabled,
      nurture_steps: settings.nurture_steps,
      hot_lead_phone: settings.hot_lead_phone,
      default_book_link: settings.default_book_link,
      updated_at: new Date().toISOString(),
    }).eq("id", 1);
    setSaving(false);
    if (error) setMsg("Error: " + error.message);
    else { setMsg("Saved!"); setTimeout(()=>setMsg(""), 2000); }
  };

  if (!settings) return <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading...</div>;

  // Surface which toggles are currently ACTIVE so the owner sees at a glance
  // that the bot is about to message leads. Critical the day Elyon goes live
  // — we don't want welcome-SMSes blasting yesterday's leads by surprise.
  const activeAutomations = [
    settings.welcome_sms_enabled  && "Welcome SMS",
    settings.welcome_email_enabled && "Welcome Email",
    settings.nurture_enabled && "Nurture sequence",
  ].filter(Boolean);

  return (
    <div>
      {activeAutomations.length > 0 && (
        <div style={{padding:"10px 14px",background:"rgba(180,140,60,0.1)",border:"1px solid rgba(180,140,60,0.35)",borderRadius:8,fontSize:12,marginBottom:14,color:"#5d4a1f"}}>
          <strong>⚠ Live automations:</strong> {activeAutomations.join(", ")}. Any new lead landing in the system right now will receive these. Toggle off below if you don't want that.
        </div>
      )}
      {msg && <div style={{padding:"10px 14px",background:msg.startsWith("Error")?C.redL:C.greenL,color:msg.startsWith("Error")?C.red:C.green,borderRadius:8,fontSize:12,fontWeight:600,marginBottom:14}}>{msg}</div>}

      <Section title="🔔 Welcome SMS">
        <Toggle label="Send welcome SMS to new leads" value={settings.welcome_sms_enabled} onChange={v=>update("welcome_sms_enabled", v)}/>
        <Field label="SMS Template (use {{first_name}}, {{interest}}, {{book_link}})">
          <textarea value={settings.welcome_sms_template} onChange={e=>update("welcome_sms_template", e.target.value)} rows={4} style={{...inp, resize:"vertical", fontFamily:"monospace", fontSize:12}}/>
        </Field>
        <Field label="Delay before sending (seconds)">
          <input type="number" min="0" value={settings.welcome_sms_delay_seconds} onChange={e=>update("welcome_sms_delay_seconds", e.target.value)} style={{...inp, maxWidth:120}}/>
        </Field>
      </Section>

      <Section title="📧 Welcome Email">
        <Toggle label="Send welcome email to new leads" value={settings.welcome_email_enabled} onChange={v=>update("welcome_email_enabled", v)}/>
        <Field label="Email Subject">
          <input value={settings.welcome_email_subject} onChange={e=>update("welcome_email_subject", e.target.value)} style={inp}/>
        </Field>
      </Section>

      <Section title="🔄 Nurture Sequence">
        <Toggle label="Send nurture messages to unbooked leads" value={settings.nurture_enabled} onChange={v=>update("nurture_enabled", v)}/>
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>{(settings.nurture_steps||[]).length} steps configured. Cron runs every hour.</div>
        {(settings.nurture_steps||[]).map((step, i) => (
          <div key={i} style={{padding:12,background:C.light,border:"1px solid "+C.border,borderRadius:8,marginBottom:8}}>
            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
              <span style={{padding:"3px 8px",background:C.gold,color:"#fff",borderRadius:50,fontSize:10,fontWeight:700}}>Day {step.day}</span>
              <span style={{padding:"3px 8px",background:step.channel==="sms"?C.blueL:C.greenL,color:step.channel==="sms"?C.blue:C.green,borderRadius:50,fontSize:10,fontWeight:700,textTransform:"uppercase"}}>{step.channel}</span>
            </div>
            {step.subject && <div style={{fontSize:11,color:C.text,fontWeight:600,marginBottom:4}}>📧 {step.subject}</div>}
            <div style={{fontSize:11,color:C.muted,whiteSpace:"pre-wrap",fontFamily:"monospace"}}>{step.content?.substring(0, 200)}{step.content?.length > 200 ? "..." : ""}</div>
          </div>
        ))}
      </Section>

      <Section title="⚙️ General">
        <Field label="Default booking link">
          <input value={settings.default_book_link} onChange={e=>update("default_book_link", e.target.value)} style={inp}/>
        </Field>
        <Field label="Hot lead phone alert (optional)">
          <input value={settings.hot_lead_phone||""} onChange={e=>update("hot_lead_phone", e.target.value)} placeholder="+1XXXXXXXXXX" style={inp}/>
        </Field>
      </Section>

      <button onClick={save} disabled={saving} style={{padding:"11px 24px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1}}>
        {saving?"Saving...":"💾 Save All Settings"}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ANALYTICS TAB
// ════════════════════════════════════════════════════════════
function AnalyticsTab() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      const sevenDaysAgo = new Date(Date.now() - 7*24*60*60*1000).toISOString();
      const thirtyDaysAgo = new Date(Date.now() - 30*24*60*60*1000).toISOString();

      const [{ count: total }, { count: last7 }, { count: last30 }, { count: hot }, { count: booked }, { data: sources }] = await Promise.all([
        supabase.from("leads").select("*", { count:"exact", head:true }),
        supabase.from("leads").select("*", { count:"exact", head:true }).gte("created_at", sevenDaysAgo),
        supabase.from("leads").select("*", { count:"exact", head:true }).gte("created_at", thirtyDaysAgo),
        supabase.from("leads").select("*", { count:"exact", head:true }).eq("lead_temperature", "hot"),
        supabase.from("leads").select("*", { count:"exact", head:true }).eq("booked", true),
        supabase.from("leads").select("source").gte("created_at", thirtyDaysAgo),
      ]);

      const bySource = {};
      (sources||[]).forEach(s => {
        const k = s.source || "unknown";
        bySource[k] = (bySource[k]||0) + 1;
      });

      setStats({
        total, last7, last30, hot, booked,
        bySource: Object.entries(bySource).sort((a,b) => b[1]-a[1]),
        conversionRate: total ? ((booked/total)*100).toFixed(1) : "0",
      });
    })();
  }, []);

  if (!stats) return <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading...</div>;

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:18}}>
        <Stat label="Total Leads" value={stats.total||0} color={C.gold}/>
        <Stat label="Last 7 Days" value={stats.last7||0} color={C.blue}/>
        <Stat label="Last 30 Days" value={stats.last30||0} color={C.purple}/>
        <Stat label="🔥 Hot" value={stats.hot||0} color={C.hot}/>
        <Stat label="Booked" value={stats.booked||0} color={C.green}/>
        <Stat label="Conversion" value={stats.conversionRate+"%"} color={C.gold}/>
      </div>

      <div style={{padding:18,background:C.card,border:"1px solid "+C.border,borderRadius:10}}>
        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:12}}>Leads by Source (last 30 days)</div>
        {stats.bySource.length === 0 ? (
          <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>No data yet</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {stats.bySource.map(([source, count]) => (
              <div key={source} style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:120,fontSize:12,fontWeight:600,color:C.text,textTransform:"capitalize"}}>{source}</div>
                <div style={{flex:1,height:20,background:C.light,borderRadius:50,overflow:"hidden",position:"relative"}}>
                  <div style={{
                    width:`${(count/stats.last30)*100}%`,
                    height:"100%",
                    background:"linear-gradient(90deg,"+C.gold+","+C.goldD+")",
                    borderRadius:50,
                  }}/>
                </div>
                <div style={{width:50,textAlign:"right",fontSize:12,fontWeight:700,color:C.gold}}>{count}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════
function Stat({ label, value, color }) {
  return (
    <div style={{padding:14,background:C.card,border:"1px solid "+C.border,borderRadius:10,borderLeft:"4px solid "+color}}>
      <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
      <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{marginBottom:18,padding:16,background:C.card,border:"1px solid "+C.border,borderRadius:10}}>
      <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:12}}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{marginBottom:12}}>
      <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div style={{marginBottom:12}}>
      <button onClick={()=>onChange(!value)} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 14px",background:value?C.greenL:C.light,color:value?C.green:C.muted,border:"1px solid "+(value?C.green+"40":C.border),borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F,textAlign:"left"}}>
        <span style={{width:36,height:20,background:value?C.green:C.dim,borderRadius:50,position:"relative",flexShrink:0}}>
          <span style={{position:"absolute",top:2,left:value?18:2,width:16,height:16,background:"#fff",borderRadius:"50%",transition:"left 0.15s"}}/>
        </span>
        <span style={{flex:1}}>{label}</span>
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// RESPONSE TIME DASHBOARD — owner/director view of how fast
// the team answers new leads. Surfaces missed + late responses.
// ════════════════════════════════════════════════════════════
function ResponseTimeDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(7);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const since = new Date(); since.setDate(since.getDate() - rangeDays);
      const { data } = await supabase
        .from("leads")
        .select("id, full_name, phone, source, created_at, first_acknowledged_at, first_acknowledged_seconds, first_acknowledged_by_name, acknowledged_action, lead_temperature, estimated_value")
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: false })
        .limit(500);
      if (active) { setLeads(data || []); setLoading(false); }
    })();
    const ch = supabase.channel("response-dashboard-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "leads" }, async () => {
        const since = new Date(); since.setDate(since.getDate() - rangeDays);
        const { data } = await supabase.from("leads").select("id, full_name, phone, source, created_at, first_acknowledged_at, first_acknowledged_seconds, first_acknowledged_by_name, acknowledged_action, lead_temperature, estimated_value").gte("created_at", since.toISOString()).order("created_at", { ascending: false }).limit(500);
        if (active) setLeads(data || []);
      }).subscribe();
    return () => { active = false; supabase.removeChannel(ch); };
  }, [rangeDays]);

  if (loading) return <div style={{padding:40,textAlign:"center",color:C.muted,fontSize:13}}>Loading response data…</div>;

  const acknowledged = leads.filter(l => l.first_acknowledged_at != null && l.first_acknowledged_seconds != null);
  const unacknowledged = leads.filter(l => l.first_acknowledged_at == null);
  const onTime = acknowledged.filter(l => l.first_acknowledged_seconds <= 60);
  const slow = acknowledged.filter(l => l.first_acknowledged_seconds > 60 && l.first_acknowledged_seconds <= 300);
  const veryLate = acknowledged.filter(l => l.first_acknowledged_seconds > 300);
  const avgSec = acknowledged.length ? Math.round(acknowledged.reduce((s, l) => s + l.first_acknowledged_seconds, 0) / acknowledged.length) : 0;

  // Per-staff breakdown
  const perStaff = {};
  acknowledged.forEach(l => {
    const name = l.first_acknowledged_by_name || "Unknown";
    if (!perStaff[name]) perStaff[name] = { count: 0, totalSec: 0, onTime: 0, late: 0 };
    perStaff[name].count++;
    perStaff[name].totalSec += l.first_acknowledged_seconds;
    if (l.first_acknowledged_seconds <= 60) perStaff[name].onTime++;
    else perStaff[name].late++;
  });

  const fmtSec = (s) => s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s/60)}m ${s%60}s` : `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`;

  return (
    <div>
      {/* Date range chips */}
      <div style={{display:"flex",gap:6,marginBottom:18,flexWrap:"wrap"}}>
        {[[1,"Today"],[7,"Last 7 days"],[30,"Last 30 days"],[90,"Last 90 days"]].map(([d,l])=>(
          <button key={d} onClick={()=>setRangeDays(d)} style={{padding:"6px 14px",fontSize:11.5,fontWeight:rangeDays===d?800:600,color:rangeDays===d?"#fff":C.muted,background:rangeDays===d?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",border:"1px solid "+(rangeDays===d?C.gold:C.border),borderRadius:99,cursor:"pointer",fontFamily:F}}>{l}</button>
        ))}
      </div>

      {/* Stat tiles */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))",gap:12,marginBottom:22}}>
        <div style={{padding:"16px 18px",background:C.card,border:"1px solid "+C.border,borderRadius:12,borderTop:"3px solid "+C.gold}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Total Leads</div>
          <div style={{fontSize:28,fontWeight:800,color:C.text,fontFamily:S,letterSpacing:"-0.02em"}}>{leads.length}</div>
        </div>
        <div style={{padding:"16px 18px",background:C.card,border:"1px solid "+C.border,borderRadius:12,borderTop:"3px solid "+C.green}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>On Time (≤1m)</div>
          <div style={{fontSize:28,fontWeight:800,color:C.green,fontFamily:S,letterSpacing:"-0.02em"}}>{onTime.length} <span style={{fontSize:14,color:C.muted,fontWeight:600}}>({leads.length?Math.round((onTime.length/leads.length)*100):0}%)</span></div>
        </div>
        <div style={{padding:"16px 18px",background:C.card,border:"1px solid "+C.border,borderRadius:12,borderTop:"3px solid #e87d1f"}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Slow (1–5m)</div>
          <div style={{fontSize:28,fontWeight:800,color:"#e87d1f",fontFamily:S,letterSpacing:"-0.02em"}}>{slow.length}</div>
        </div>
        <div style={{padding:"16px 18px",background:C.card,border:"1px solid "+C.border,borderRadius:12,borderTop:"3px solid "+C.red}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Very Late (5m+)</div>
          <div style={{fontSize:28,fontWeight:800,color:C.red,fontFamily:S,letterSpacing:"-0.02em"}}>{veryLate.length}</div>
        </div>
        <div style={{padding:"16px 18px",background:C.card,border:"1px solid "+C.border,borderRadius:12,borderTop:"3px solid "+C.red}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Never Acknowledged</div>
          <div style={{fontSize:28,fontWeight:800,color:unacknowledged.length>0?C.red:C.muted,fontFamily:S,letterSpacing:"-0.02em"}}>{unacknowledged.length}</div>
        </div>
        <div style={{padding:"16px 18px",background:C.card,border:"1px solid "+C.border,borderRadius:12,borderTop:"3px solid "+C.gold}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Avg Response</div>
          <div style={{fontSize:28,fontWeight:800,color:C.gold,fontFamily:S,letterSpacing:"-0.02em"}}>{acknowledged.length?fmtSec(avgSec):"—"}</div>
        </div>
      </div>

      {/* Per-staff table */}
      {Object.keys(perStaff).length > 0 && (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:18,marginBottom:22}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,letterSpacing:"0.02em",marginBottom:12}}>👥 Performance by Staff</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {Object.entries(perStaff).sort((a,b)=>(a[1].totalSec/a[1].count) - (b[1].totalSec/b[1].count)).map(([name, stats])=>{
              const avg = Math.round(stats.totalSec / stats.count);
              const onTimePct = Math.round((stats.onTime / stats.count) * 100);
              return (
                <div key={name} style={{padding:"10px 14px",background:C.light,borderRadius:9,border:"1px solid "+C.border,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:140}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{name}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>{stats.count} lead{stats.count!==1?"s":""} answered</div>
                  </div>
                  <div style={{textAlign:"center",minWidth:80}}>
                    <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Avg</div>
                    <div style={{fontSize:14,fontWeight:800,color:avg<=60?C.green:avg<=300?"#e87d1f":C.red}}>{fmtSec(avg)}</div>
                  </div>
                  <div style={{textAlign:"center",minWidth:80}}>
                    <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>On Time</div>
                    <div style={{fontSize:14,fontWeight:800,color:onTimePct>=80?C.green:onTimePct>=50?"#e87d1f":C.red}}>{onTimePct}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent leads list with response time */}
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:18}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,letterSpacing:"0.02em",marginBottom:12}}>📋 Recent Leads — Response Detail</div>
        {leads.length === 0 ? (
          <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>No leads in this range</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:520,overflowY:"auto"}}>
            {leads.map(l => {
              const acked = l.first_acknowledged_at != null && l.first_acknowledged_seconds != null;
              const sec = l.first_acknowledged_seconds;
              const color = !acked ? C.red : sec <= 60 ? C.green : sec <= 300 ? "#e87d1f" : C.red;
              const label = !acked ? "❌ Never answered" : sec <= 60 ? "✓ On time" : sec <= 300 ? "⚠ Slow" : "🚨 Very late";
              return (
                <div key={l.id} style={{padding:"10px 12px",background:C.light,borderRadius:8,border:"1px solid "+C.border,borderLeft:"3px solid "+color,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:140}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{l.full_name || "Unnamed lead"}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:1}}>
                      {l.source ? `via ${l.source} · ` : ""}{new Date(l.created_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                      {l.first_acknowledged_by_name ? ` · answered by ${l.first_acknowledged_by_name}` : ""}
                    </div>
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:color,padding:"3px 10px",background:"#fff",borderRadius:99,border:"1px solid "+color+"40",whiteSpace:"nowrap"}}>
                    {label}{acked?` · ${fmtSec(sec)}`:""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
